import React from 'react'
import { usePrevious } from './usePrevious'

const storage: Storage | null = 'localStorage' in global ? (global as any).localStorage : null

export function useViewState<T>(key: string, initialValue: T): ViewStateHook<T> {
  const prevKey = usePrevious(key)

  const getValueFromStorage = React.useCallback(() => {
    const serialized = storage?.getItem(key)
    if (serialized == null) { return initialValue }

    try {
      return JSON.parse(serialized) as T
    } catch {
      return initialValue
    }
  }, [initialValue, key])

  const [cache, setCache] = React.useState<T | undefined>(undefined)

  // The value uses the cached value if it exists, unless the key has changed. Then it uses the storage value.
  const value = React.useMemo(() => {
    if (prevKey === key && cache !== undefined) {
      return cache
    } else {
      return getValueFromStorage()
    }
  }, [cache, getValueFromStorage, key, prevKey])

  // Reset the cached state value if the key changes.
  React.useEffect(() => {
    if (key !== prevKey) { setCache(value) }
  }, [key, prevKey, value])

  // When setting the value, update local storage and the local cache.
  const setValue = React.useCallback((value: T) => {
    storage?.setItem(key, JSON.stringify(value))
    setCache(value)
  }, [key])

  return [value, setValue]
}

export type ViewStateHook<T> = [
  T,
  (value: T) => void
]